import ApiService, { type ApiResponse } from './api-services';

interface OrganizationData {
  org_id: string;
  name: string;
  slug?: string | null;
  stripe_plan_name?: string | null;
  stripe_subscription_status?: string | null;
  current_period_end?: string | null;
  max_members: number;
  created_at?: string;
  updated_at?: string;
}

interface InvitationData {
  org_id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string | null;
}

/**
 * Pricing plan data for an organization.
 *
 * TODO(billing): admin_fee_eur and seat_price_eur are hardcoded on the backend
 * for testing. Replace with real Stripe values once Phase 3 is complete (#1530).
 * WARNING: Do NOT use these values for actual billing until Stripe is integrated.
 */
interface OrgPlanData {
  org_id: string;
  plan_tag: string | null;
  // Org lifecycle status (setup | dpa_pending | dpa_signed | active |
  // suspended) — mirrors OrgPlanResponse.status. `suspended` means the
  // Stripe subscription ended and paid features are blocked (#2544).
  status: string | null;
  admin_fee_eur: number;
  seat_price_eur: number;
  seat_count: number;
  /** Org admin's MFA opt-in (issue #2202). */
  require_mfa: boolean;
  /**
   * #1887 — days a member has after the later of (require_mfa turned on,
   * they joined) before AAL1 sessions are blocked. 0 = immediate.
   */
  mfa_grace_period_days: number;
  /** ISO instant require_mfa last flipped on; null while off (#1887). */
  mfa_required_since: string | null;
  stripe_subscription_status: string | null;
  current_period_end: string | null;
  /**
   * #11314 — the admin cancelled through the Stripe portal
   * (`cancel_at_period_end`): the subscription is still live (the status
   * above stays `active`) but will not renew, and `current_period_end` is the
   * access-END date, not a next-invoice date. Read live off Stripe only when
   * the plan is requested with `includeCancellation`; `false` otherwise or
   * when that read failed.
   */
  cancellation_pending: boolean;
  /** When paid access ends if the cancellation stands; null when not pending or unknown. */
  cancel_at: string | null;
  stripe_price_id: string | null;
  stripe_admin_fee_price_id: string | null;
  price_tiers: Array<{ up_to: number | null; unit_amount_eur: number }> | null;
  overflow_rate_eur: number | null;
}

interface AvailableOrgPlan {
  // NOT PlanTag: this shape comes from GET /organizations/available-plans,
  // which filters on is_active=true (not is_visible=true like PlanTag's
  // generator), so it still returns retired is_visible=false org SKUs
  // (plus_org, starter_org) for the legacy org-checkout surface. See #10815.
  tag: string;
  plan_name: string;
  seat_price_eur: number;
  admin_fee_eur: number;
  overflow_rate_eur: number | null;
  daily_standard_limit: number | null;
  daily_full_limit: number | null;
  daily_deep_limit: number | null;
  monthly_soft_cap: number | null;
  max_storage_mb: number;
  price_tiers: Array<{ up_to: number | null; unit_amount_eur: number }> | null;
  stripe_price_id: string | null;
  stripe_admin_fee_price_id: string | null;
  stripe_annual_price_id: string | null;
  annual_price_eur: number | null;
  billing_mode: string | null;
}

interface OrgMember {
  user_id: string;
  role: string;
  identifier: string | null;
  first_name: string | null;
  last_name: string | null;
  joined_at: string | null;
  /** The member has a VERIFIED TOTP factor in Supabase Auth (#1887). */
  mfa_enabled: boolean;
  /**
   * ISO instant this member's grace period ends (org policy start or their
   * join date, whichever is later, plus the org's grace days); null when the
   * org does not require MFA. Same formula the JWT hook enforces (#1887).
   */
  mfa_deadline: string | null;
}

/** Org-level MFA requirement as the admin configured it (#1887). */
interface OrgMfaPolicy {
  require_mfa: boolean;
  grace_period_days: number;
  required_since: string | null;
}

interface OrgMembersData {
  org_id: string;
  members: OrgMember[];
  total: number;
  mfa_policy: OrgMfaPolicy;
}

/**
 * How the Stripe quantity sync went (#2545) — mirrors `SeatSyncOutcome` in
 * `server/constants/org_billing.py`. `payment_failed` never arrives on a
 * success body: the backend answers 402 with
 * `detail.code === ORG_SEAT_PAYMENT_FAILED_CODE` instead, and a failed
 * increase answers 502 without granting the seats.
 */
type SeatSyncOutcome =
  | 'synced'
  | 'unchanged'
  | 'skipped_no_subscription'
  | 'skipped_no_item'
  | 'skipped_stripe_disabled'
  | 'stripe_error';

interface UpdateSeatsData {
  org_id: string;
  max_members: number;
  previous_max_members: number;
  stripe_sync: SeatSyncOutcome;
}

/**
 * `detail.code` the backend puts on its 402 when Stripe declines the immediate
 * charge for added seats (`OrgSeatErrorCode.PAYMENT_FAILED`,
 * `server/constants/org_billing.py`). No seat was granted server-side.
 */
export const ORG_SEAT_PAYMENT_FAILED_CODE = 'ORG_SEAT_PAYMENT_FAILED';

interface OrgQuotaData {
  org_id: string;
  period_start: string | null;
  period_end: string | null;
  queries_used: number;
  queries_limit: number | null;
  credits_used: number;
  credits_limit: number | null;
}

interface UsageHistoryMember {
  user_id: string;
  name: string | null;
  count: number;
}

interface UsageHistoryDay {
  date: string;
  total: number;
  members: UsageHistoryMember[];
}

interface UsageHistoryData {
  days: UsageHistoryDay[];
  period_start: string;
  period_end: string;
}

interface OrgCheckoutSessionData {
  clientSecret: string;
}

/** Org customer type for invoicing (#2605). */
type OrgBillingEntityType = 'person' | 'business';

/**
 * The organization's own billing identity (#2605) — the customer block of
 * SK-compliant org invoices. Null fields mean "not captured yet" (orgs
 * onboarded before the identity capture landed).
 */
interface OrgBillingIdentity {
  org_id: string;
  entity_type: OrgBillingEntityType | null;
  is_vat_payer: boolean | null;
  business_name: string | null;
  ico: string | null;
  dic: string | null;
  ic_dph: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
}

/** PUT payload — full-replace form state for the org billing identity. */
interface OrgBillingIdentityUpdate {
  entity_type: OrgBillingEntityType;
  is_vat_payer: boolean;
  business_name?: string | null;
  ico?: string | null;
  dic?: string | null;
  ic_dph?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

interface OrgInvoice {
  id: string;
  status: string;
  total: number;
  total_eur: number;
  currency: string;
  pdf_url: string | null;
  hosted_url: string | null;
  invoice_number: string | null;
  period_start: string | null;
  period_end: string | null;
}

interface AuditLogEntry {
  id: string;
  action: string;
  actor_user_id: string;
  actor_name: string | null;
  target_user_id: string | null;
  target_name: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  ip_address: string | null;
}

interface AuditLogData {
  entries: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

interface OrgGroup {
  id: string;
  org_id: string;
  name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  member_count: number;
}

interface OrgGroupMember {
  group_id: string;
  user_id: string;
  added_by: string;
  added_at: string;
}

class OrganizationsService extends ApiService {
  constructor() {
    super('/organizations');
  }

  /**
   * Returns all organizations the authenticated user belongs to.
   * Used by the org switcher to list available orgs.
   */
  getUserOrganizations(): ApiResponse<OrganizationData[]> {
    return this.getData('/me');
  }

  /**
   * Creates a new organization.
   * The caller becomes the org admin automatically.
   */
  createOrganization(
    name: string,
    slug?: string
  ): ApiResponse<OrganizationData> {
    return this.postData('', { name, ...(slug ? { slug } : {}) });
  }

  /**
   * Switches the active organization for the current user.
   * After calling this, refresh the Supabase session to get updated app_metadata.
   */
  switchOrganization(orgId: string): ApiResponse<void> {
    return this.postData(`/${orgId}/switch`, {});
  }

  /**
   * Updates an organization's name.
   * Requires the caller to be an admin.
   */
  updateOrganization(
    orgId: string,
    name: string
  ): ApiResponse<OrganizationData> {
    return this.patchData(`/${orgId}`, { name });
  }

  /**
   * Updates organization settings (MFA, data retention, etc.).
   * Requires the caller to be an admin.
   */
  updateOrgSettings(
    orgId: string,
    settings: {
      require_mfa?: boolean;
      mfa_grace_period_days?: number;
      data_retention_days?: number | null;
    }
  ): ApiResponse<OrganizationData> {
    return this.patchData(`/${orgId}`, settings);
  }

  /**
   * Deletes an organization. Requires the caller to be an admin.
   */
  deleteOrganization(orgId: string): ApiResponse<void> {
    return this.deleteData(`/${orgId}`, {});
  }

  // ─── Invitations ──────────────────────────────────────────────────────────

  /**
   * Invites a user to the organization by email.
   * Requires the caller to be an admin.
   */
  inviteMember(orgId: string, email: string): ApiResponse<void> {
    return this.postData(`/${orgId}/invitations`, { email });
  }

  /**
   * Lists pending invitations for the organization.
   * Requires the caller to be an admin.
   */
  listInvitations(orgId: string): ApiResponse<InvitationData[]> {
    return this.getData(`/${orgId}/invitations`);
  }

  /**
   * Revokes a pending invitation.
   * Requires the caller to be an admin.
   */
  revokeInvitation(orgId: string, email: string): ApiResponse<void> {
    return this.deleteData(`/${orgId}/invitations`, { email });
  }

  /**
   * Decodes a signed invitation token, returning org_id, email, org_name.
   */
  decodeInvitationToken(
    token: string
  ): ApiResponse<{ org_id: string; email: string; org_name: string }> {
    return this.getData(
      `/invitations/decode-token?token=${encodeURIComponent(token)}`
    );
  }

  /**
   * Checks if a pending invitation exists for the given email in the org.
   * No authentication required.
   */
  checkInvitation(
    orgId: string,
    email: string
  ): ApiResponse<{ exists: boolean }> {
    return this.getData(
      `/${orgId}/invitations/check?email=${encodeURIComponent(email)}`
    );
  }

  /**
   * Accepts an invitation to join the organization.
   * The caller must be the invited user.
   */
  acceptInvitation(orgId: string): ApiResponse<void> {
    return this.postData(`/${orgId}/invitations/accept`, {});
  }

  /**
   * Declines an invitation to join the organization.
   * Deletes the invitation so it no longer appears in the admin's list.
   */
  declineInvitation(orgId: string): ApiResponse<void> {
    return this.postData(`/${orgId}/invitations/decline`, {});
  }

  // ─── Member management ────────────────────────────────────────────────────

  /**
   * Removes a member from the organization.
   * Requires the caller to be an admin. Cannot remove last admin.
   */
  removeMember(orgId: string, userId: string): ApiResponse<void> {
    return this.deleteData(`/${orgId}/members/${userId}`, {});
  }

  /**
   * Changes a member's role (e.g. 'org:owner' or 'org:member').
   * Requires the caller to be an admin. Cannot demote last admin.
   */
  changeMemberRole(
    orgId: string,
    userId: string,
    role: string
  ): ApiResponse<void> {
    return this.patchData(`/${orgId}/members/${userId}/role`, { role });
  }

  /**
   * Returns pricing plan data for a specific organization.
   * Values are hardcoded on the backend for testing — see OrgPlanData warning.
   */
  getOrganizationPlan(
    orgId: string,
    options: { includeCancellation?: boolean } = {}
  ): ApiResponse<OrgPlanData | null> {
    // #11314: the live Stripe cancellation read is opt-in. OrgSetupBanner hits
    // this route on every app page for an org owner and needs only `status`.
    const query = options.includeCancellation
      ? '?include_cancellation=true'
      : '';
    return this.getData(`/${orgId}/plan${query}`);
  }

  /**
   * Update organization onboarding status.
   * Valid transitions: setup→dpa_pending, dpa_pending→dpa_signed, dpa_signed→active
   */
  updateOrgStatus(
    orgId: string,
    newStatus: string
  ): ApiResponse<{ status: string } | null> {
    return this.patchData(`/${orgId}/status`, { status: newStatus });
  }

  /**
   * Save selected plan + seat count during onboarding.
   * Persists in DB so it survives page refreshes.
   */
  saveSelectedPlan(
    orgId: string,
    planTag: string,
    seatCount: number
  ): ApiResponse<{ plan_tag: string; seat_count: number } | null> {
    return this.patchData(`/${orgId}/selected-plan`, {
      plan_tag: planTag,
      seat_count: seatCount,
    });
  }

  /**
   * Get selected plan + seat count from onboarding.
   */
  getSelectedPlan(orgId: string): ApiResponse<{
    plan_tag: string | null;
    seat_count: number | null;
  } | null> {
    return this.getData(`/${orgId}/selected-plan`);
  }

  /**
   * Returns all available organization plans from the plans table.
   * Used by org onboarding to let admin choose Starter Org vs Plus Org.
   */
  getAvailableOrgPlans(): ApiResponse<{
    plans: AvailableOrgPlan[];
    admin_fee_waived: boolean;
  } | null> {
    return this.getData('/available-plans');
  }

  /**
   * Returns the list of members for a specific organization.
   * Requires the caller to be an admin of the organization.
   */
  getOrgMembers(orgId: string): ApiResponse<OrgMembersData | null> {
    return this.getData(`/${orgId}/members`);
  }

  /**
   * Updates the purchased seat count for an organization.
   * Requires the caller to be an admin of the organization.
   */
  updateSeats(
    orgId: string,
    seatCount: number
  ): ApiResponse<UpdateSeatsData | null> {
    return this.patchData(`/${orgId}/seats`, { seat_count: seatCount });
  }

  /**
   * Returns the current quota usage for a specific organization.
   * Requires the caller to be an admin of the organization.
   */
  getOrgQuota(orgId: string): ApiResponse<OrgQuotaData | null> {
    return this.getData(`/${orgId}/quota`);
  }

  /**
   * Returns daily usage history for an organization, grouped by day and member.
   * Requires the caller to be an org owner.
   */
  getUsageHistory(orgId: string, days = 30): ApiResponse<UsageHistoryData> {
    return this.getData(`/${orgId}/usage-history?days=${days}`);
  }

  /**
   * Creates a Stripe Customer Portal session for the organization.
   * Returns a portal_url the caller should redirect to immediately.
   * The portal lets admins update payment method, view invoices, cancel plan.
   */
  createPortalSession(
    orgId: string
  ): ApiResponse<{ portal_url: string } | null> {
    return this.postData(`/${orgId}/portal`, {});
  }

  /**
   * Creates a Stripe Checkout session for subscribing an organization.
   * Calls the Next.js internal API route (not the Python backend).
   * Returns a clientSecret for the Stripe embedded checkout element.
   */
  async createOrgCheckoutSession(
    orgId: string,
    seatCount: number,
    locale?: string,
    userEmail?: string
  ): Promise<OrgCheckoutSessionData> {
    const response = await fetch(
      `${window.location.origin}/api/stripe/create-org-checkout-session`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, seatCount, locale, userEmail }),
      }
    );

    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      const text = await response.text();
      throw new Error(
        `Server returned non-JSON response (${response.status}): ${text.substring(0, 100)}`
      );
    }

    if (!response.ok) {
      const errorData = (await response.json()) as { error?: string };
      throw new Error(
        errorData.error ?? 'Failed to create org checkout session'
      );
    }

    return (await response.json()) as OrgCheckoutSessionData;
  }

  // ─── Billing identity (#2605) ──────────────────────────────────────────────

  /**
   * Returns the organization's own billing identity (entity type, VAT-payer
   * flag, ICO/DIC/IC DPH, billing address). Requires org owner.
   */
  getOrgBillingIdentity(orgId: string): ApiResponse<OrgBillingIdentity | null> {
    return this.getData(`/${orgId}/billing-identity`);
  }

  /**
   * Persists the organization's billing identity — written by onboarding
   * Step 2 and editable from the billing tab. Requires org owner.
   */
  updateOrgBillingIdentity(
    orgId: string,
    payload: OrgBillingIdentityUpdate
  ): ApiResponse<OrgBillingIdentity | null> {
    return this.putData(`/${orgId}/billing-identity`, payload);
  }

  // ─── Invoices ──────────────────────────────────────────────────────────────

  /**
   * Returns invoices for the organization from the local invoice ledger
   * (`public.invoices`, fed by the `invoice.paid` webhook — #11657).
   * Requires the caller to be the org owner.
   */
  getOrgInvoices(orgId: string): ApiResponse<{ invoices: OrgInvoice[] }> {
    return this.getData(`/${orgId}/invoices`);
  }

  // ─── Audit log ──────────────────────────────────────────────────────────────

  /**
   * Returns paginated audit log entries for an organization.
   * Requires the caller to be an admin of the organization.
   */
  getAuditLog(
    orgId: string,
    limit = 50,
    offset = 0
  ): ApiResponse<AuditLogData> {
    return this.getData(`/${orgId}/audit-log?limit=${limit}&offset=${offset}`);
  }

  // ─── Groups ────────────────────────────────────────────────────────────────

  /**
   * Returns all groups for a specific organization.
   * Requires the caller to be an admin of the organization.
   */
  getOrgGroups(orgId: string): ApiResponse<OrgGroup[]> {
    return this.getData(`/${orgId}/groups`);
  }

  /**
   * Creates a new group for the organization.
   * Requires the caller to be an admin of the organization.
   */
  createGroup(orgId: string, name: string): ApiResponse<OrgGroup> {
    return this.postData(`/${orgId}/groups`, { name });
  }

  /**
   * Renames an existing group.
   * Requires the caller to be an admin of the organization.
   */
  renameGroup(
    orgId: string,
    groupId: string,
    name: string
  ): ApiResponse<OrgGroup> {
    return this.patchData(`/${orgId}/groups/${groupId}`, { name });
  }

  /**
   * Deletes a group and removes all its members.
   * Requires the caller to be an admin of the organization.
   */
  deleteGroup(orgId: string, groupId: string): ApiResponse<void> {
    return this.deleteData(`/${orgId}/groups/${groupId}`, {});
  }

  /**
   * Adds a member to a group.
   * Requires the caller to be an admin of the organization.
   */
  addGroupMember(
    orgId: string,
    groupId: string,
    userId: string
  ): ApiResponse<void> {
    return this.postData(`/${orgId}/groups/${groupId}/members`, {
      user_id: userId,
    });
  }

  /**
   * Removes a member from a group.
   * Requires the caller to be an admin of the organization.
   */
  removeGroupMember(
    orgId: string,
    groupId: string,
    userId: string
  ): ApiResponse<void> {
    return this.deleteData(`/${orgId}/groups/${groupId}/members/${userId}`, {});
  }

  // DPA upload + admin-review flow lives in @/services/org-dpa-services.ts.
  // The legacy click-to-sign signDpa / getDpaStatus methods were removed
  // along with the Gen-1 backend endpoints they called.
}

const organizationsService = new OrganizationsService();
export default organizationsService;
export type {
  OrganizationData,
  InvitationData,
  OrgPlanData,
  AvailableOrgPlan,
  OrgMember,
  OrgMfaPolicy,
  OrgMembersData,
  UpdateSeatsData,
  OrgQuotaData,
  OrgCheckoutSessionData,
  OrgBillingEntityType,
  OrgBillingIdentity,
  OrgBillingIdentityUpdate,
  OrgInvoice,
  AuditLogEntry,
  AuditLogData,
  OrgGroup,
  OrgGroupMember,
  UsageHistoryMember,
  UsageHistoryDay,
  UsageHistoryData,
};
